import React, { createContext, useContext, useState } from 'react';

export interface SpinnerStyle {
  id: string;
  name: string;
  icon?: string;
  sound?: string;
}

const spinnerStyles: SpinnerStyle[] = [
  { id: 'colabottle', name: 'Cola Bottle', icon: '/images/colabottle.png', sound: '/sounds/beer1.mp3' },
  { id: 'orangesodabottle', name: 'Orange Soda Bottle', icon: '/images/orangesodabottle.png', sound: '/sounds/beer.mp3' },
  { id: 'waterbottle', name: 'Water Bottle', icon: '/images/waterbottle.png', sound: '/sounds/waterbottle.mp3' },
  { id: 'beerbottle', name: 'Beer Bottle', icon: '/images/beerbottle.png', sound: '/sounds/beer.mp3' },
  { id: 'whiskybottle', name: 'Whisky Bottle', icon: '/images/whiskybottle.png', sound: '/sounds/wine.mp3' },
  { id: 'champagne', name: 'Champagne', icon: '/images/champagne.png', sound: '/sounds/champagne.mp3' },
  { id: 'wheel', name: 'Wheel', sound: '/sounds/wheel.mp3' },
  { id: 'pencil', name: 'Pencil', icon: '/images/pencil.png', sound: '/sounds/pencil.mp3' },
  { id: 'pen', name: 'Pen', icon: '/images/pen.png', sound: '/sounds/pencil.mp3' },
  { id: 'compass', name: 'Compass', icon: '/images/compass.png', sound: '/sounds/compass.mp3' },
  { id: 'knife', name: 'Knife', icon: '/images/knife.png', sound: '/sounds/knife.mp3' },
  { id: 'arrow', name: 'Arrow', icon: '/images/arrow.png', sound: '/sounds/arrow.mp3' },
  { id: 'magic-wand', name: 'Magic Wand', icon: '/images/magic-wand.png', sound: '/sounds/magic-wand.mp3' },
];

interface SpinnerContextType {
  spinnerStyles: SpinnerStyle[];
  selectedSpinner: SpinnerStyle;
  setSelectedSpinner: (spinner: SpinnerStyle) => void;

  spinRequested: boolean;
  setSpinRequested: (spin: boolean) => void;
  isSidebarLocked: boolean;
  setIsSidebarLocked: (locked: boolean) => void;
  spinning: boolean;
  setSpinning: (spinning: boolean) => void;
  winnerIndex: number | null;
  setWinnerIndex: (index: number | null) => void;
}

const SpinnerContext = createContext<SpinnerContextType | undefined>(undefined);

export const SpinnerProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedSpinner, setSelectedSpinner] = useState<SpinnerStyle>(spinnerStyles[0]);
  const [spinRequested, setSpinRequested] = useState(false);
  const [isSidebarLocked, setIsSidebarLocked] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [winnerIndex, setWinnerIndex] = useState<number | null>(null);

  return (
    <SpinnerContext.Provider value={{ 
      spinnerStyles, 
      selectedSpinner, 
      setSelectedSpinner,
      spinRequested,
      setSpinRequested,
      isSidebarLocked,
      setIsSidebarLocked,
      spinning,
      setSpinning,
      winnerIndex,
      setWinnerIndex
    }}>
      {children}
    </SpinnerContext.Provider>
  );
};

export const useSpinner = () => {
  const context = useContext(SpinnerContext);
  if (!context) throw new Error('useSpinner must be used within a SpinnerProvider');
  return context;
};

export const colorOptions = [
  { name: '#EA4335', label: 'Red', key: 'red' },
  { name: '#0080FF', label: 'Blue', key: 'blue' },
  { name: '#FF7F00', label: 'Orange', key: 'orange' },
  { name: '#008000', label: 'Green', key: 'green' },
  { name: '#FFD700', label: 'Yellow', key: 'yellow' },
  { name: '#C5A3FF', label: 'Purple', key: 'purple' },
  { name: '#FF99FF', label: 'Pink', key: 'pink' },
  { name: '#a04646', label: 'Brown', key: 'brown' },
  { name: '#e30b5d', label: 'Raspberry', key: 'raspberry' },
  { name: '#008080', label: 'Teal', key: 'teal' },
  { name: '#1a53ff', label: 'Navy', key: 'navy' },
  { name: '#e8f48c', label: 'Lime', key: 'lime' },
  { name: '#9966cc', label: 'Indigo', key: 'indigo' },
  { name: '#ff66ff', label: 'Magenta', key: 'magenta' },
  { name: '#808000', label: 'Olive', key: 'olive' },
  { name: '#FFB6B9', label: 'Rose', key: 'rose' },
  { name: '#A8E6CF', label: 'Mint', key: 'mint' },
  { name: '#7FB3FF', label: 'Sky Blue', key: 'skyBlue' },
  { name: '#FFA07A', label: 'Salmon', key: 'salmon' },
  { name: '#FFE5D4', label: 'Cream', key: 'cream' },
  { name: '#F67280', label: 'Watermelon', key: 'watermelon' },
  { name: '#4ECDC4', label: 'Turquoise', key: 'turquoise' },
  { name: '#F8B195', label: 'Peach', key: 'peach' },
  { name: '#B8A6FF', label: 'Lavender', key: 'lavender' },
];

export const colorSkinOptions = [
  { name: '#FDBCB4', label: 'Light Peach', key: 'lightPeach' },
  { name: '#F4C2A1', label: 'Peach', key: 'peach' },
  { name: '#E8A87C', label: 'Apricot', key: 'apricot' },
  { name: '#D4A574', label: 'Light Tan', key: 'lightTan' },
  { name: '#C19A6B', label: 'Tan', key: 'tan' },
  { name: '#B8860B', label: 'Golden', key: 'golden' },
  { name: '#A0522D', label: 'Sienna', key: 'sienna' },
  { name: '#9A4A2A', label: 'Rust', key: 'rust' },
  { name: '#8B4513', label: 'Saddle Brown', key: 'saddleBrown' },
  { name: '#8B7355', label: 'Coyote Brown', key: 'coyoteBrown' },
  { name: '#6B4423', label: 'Coffee', key: 'coffee' },
  { name: '#654321', label: 'Dark Brown', key: 'darkBrown' },
  { name: '#5D4037', label: 'Brown', key: 'brown' },
  { name: '#4A2C2A', label: 'Mahogany', key: 'mahogany' },
  { name: '#4E342E', label: 'Darker Brown', key: 'darkerBrown' },
  { name: '#3C2414', label: 'Dark Mahogany', key: 'darkMahogany' },
  { name: '#3E2723', label: 'Charcoal', key: 'charcoal' },
  { name: '#2F1B14', label: 'Darkest Brown', key: 'darkestBrown' },
  { name: '#2E1B14', label: 'Deep Brown', key: 'deepBrown' },
  { name: '#1A0E0A', label: 'Black Brown', key: 'blackBrown' },
  { name: '#1B0F0B', label: 'Ebony', key: 'ebony' },
  { name: '#F5DEB3', label: 'Wheat', key: 'wheat' },
  { name: '#DEB887', label: 'Burlywood', key: 'burlywood' },
  { name: '#CD853F', label: 'Peru', key: 'peru' }
];

export const colorEyesOptions = [
  { name: '#8B4513', label: 'Brown', key: 'brown' },
  { name: '#654321', label: 'Dark Brown', key: 'darkBrown' },
  { name: '#4A2C2A', label: 'Darker Brown', key: 'darkerBrown' },
  { name: '#2F1B14', label: 'Black', key: 'black' },
  { name: '#87CEEB', label: 'Sky Blue', key: 'skyBlue' },
  { name: '#4682B4', label: 'Steel Blue', key: 'steelBlue' },
  { name: '#1E90FF', label: 'Dodger Blue', key: 'dodgerBlue' },
  { name: '#0000FF', label: 'Blue', key: 'blue' },
  { name: '#000080', label: 'Navy Blue', key: 'navyBlue' },
  { name: '#90EE90', label: 'Light Green', key: 'lightGreen' },
  { name: '#32CD32', label: 'Lime Green', key: 'limeGreen' },
  { name: '#228B22', label: 'Forest Green', key: 'forestGreen' },
  { name: '#006400', label: 'Dark Green', key: 'darkGreen' },
  { name: '#FFD700', label: 'Amber', key: 'amber' },
  { name: '#FFA500', label: 'Orange', key: 'orange' },
  { name: '#FF8C00', label: 'Dark Orange', key: 'darkOrange' },
  { name: '#DDA0DD', label: 'Plum', key: 'plum' },
  { name: '#DA70D6', label: 'Orchid', key: 'orchid' },
  { name: '#9370DB', label: 'Medium Purple', key: 'mediumPurple' },
  { name: '#8A2BE2', label: 'Blue Violet', key: 'blueViolet' },
  { name: '#4B0082', label: 'Indigo', key: 'indigo' },
  { name: '#FF69B4', label: 'Hot Pink', key: 'hotPink' },
  { name: '#DC143C', label: 'Crimson', key: 'crimson' },
  { name: '#B22222', label: 'Fire Brick', key: 'fireBrick' }
];

export const colorHairOptions = [
  { name: '#F5F5DC', label: 'Platinum', key: 'platinum' },
  { name: '#F0E68C', label: 'Blonde', key: 'blonde' },
  { name: '#DAA520', label: 'Golden', key: 'golden' },
  { name: '#CD853F', label: 'Honey', key: 'honey' },
  { name: '#B8860B', label: 'Dark Blonde', key: 'darkBlonde' },
  { name: '#A0522D', label: 'Light Brown', key: 'lightBrown' },
  { name: '#8B4513', label: 'Brown', key: 'brown' },
  { name: '#654321', label: 'Dark Brown', key: 'darkBrown' },
  { name: '#4A2C2A', label: 'Darker Brown', key: 'darkerBrown' },
  { name: '#2F1B14', label: 'Black', key: 'black' },
  { name: '#8B0000', label: 'Dark Red', key: 'darkRed' },
  { name: '#DC143C', label: 'Red', key: 'red' },
  { name: '#FF4500', label: 'Orange Red', key: 'orangeRed' },
  { name: '#FF8C00', label: 'Orange', key: 'orange' },
  { name: '#FFD700', label: 'Yellow', key: 'yellow' },
  { name: '#9370DB', label: 'Purple', key: 'purple' },
  { name: '#8A2BE2', label: 'Blue Violet', key: 'blueViolet' },
  { name: '#4B0082', label: 'Indigo', key: 'indigo' },
  { name: '#0000FF', label: 'Blue', key: 'blue' },
  { name: '#00CED1', label: 'Teal', key: 'teal' },
  { name: '#32CD32', label: 'Green', key: 'green' },
  { name: '#FF69B4', label: 'Pink', key: 'pink' },
  { name: '#C0C0C0', label: 'Silver', key: 'silver' },
  { name: '#708090', label: 'Gray', key: 'gray' }
];

export const colors = colorOptions.map(c => c.name);
export const skinColors = colorSkinOptions.map(c => c.name);
export const eyeColors = colorEyesOptions.map(c => c.name);
export const hairColors = colorHairOptions.map(c => c.name);